import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import { getAirports, Airport, AirportApiError } from '../../services/airportApi';
import { useAuth } from '../../contexts/AuthContext';
import { useModalOpenRegistration } from '../../contexts/ModalOpenContext';
import { hasRole } from '../../services/authApi';
import { getDefaultProjectStartDateString } from '../../utils/dateUtils';

interface CreateProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onCreate: (data: { name: string; projectStartDate: string; airportId: number }) => Promise<void>;
}

const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({
  open,
  onClose,
  onCreate,
}) => {
  useModalOpenRegistration(open);
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [projectName, setProjectName] = useState('');
  const [projectStartDate, setProjectStartDate] = useState<string>(() => getDefaultProjectStartDateString());
  const [selectedAirportId, setSelectedAirportId] = useState<number | ''>('');
  const [airports, setAirports] = useState<Airport[]>([]);
  const [loadingAirports, setLoadingAirports] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  
  // Check if user is Airport Manager
  const isManager = user ? hasRole(user, 'Airport Manager') : false;

  const loadAirports = useCallback(async (retryCount = 0): Promise<void> => {
    // Don't attempt to load if not authenticated
    if (!isAuthenticated) {
      console.warn('Cannot load airports: user not authenticated');
      return;
    }

    setLoadingAirports(true);
    // Clear error on retry
    if (retryCount > 0) {
      setError(null);
    }
    
    try {
      const response = await getAirports();
      setAirports(response.airports);
      
      // If user is Airport Manager, auto-set their airport
      if (isManager && user?.userId && response.airports.length > 0) {
        const userAirport = response.airports.find(
          (airport) => airport.managers?.some((m) => m.userId === user.userId)
        );
        if (userAirport) {
          setSelectedAirportId(userAirport.airportId);
        }
      }
      
      setError(null); // Clear any previous errors on success
      setLoadingAirports(false);
    } catch (err) {
      const apiError = err as AirportApiError;
      console.error('Error loading airports:', err);
      
      // Handle authentication errors with retry logic
      if (apiError.code === 'AUTH_REQUIRED') {
        // If auth is required but we think we're authenticated, wait and retry
        // This handles the race condition where cookies aren't set yet
        if (retryCount < 2 && isAuthenticated) {
          const delay = Math.pow(2, retryCount) * 500; // 500ms, 1000ms
          console.warn(`Auth not ready, retrying airports load in ${delay}ms (attempt ${retryCount + 1}/2)`);
          setTimeout(() => {
            loadAirports(retryCount + 1);
          }, delay);
          // Keep loading state true during retry - don't clear airports or set error
          return;
        } else {
          // Max retries reached or not authenticated
          setError('Authentication required. Please refresh the page and try again.');
          setAirports([]);
          setLoadingAirports(false);
        }
      } else {
        // Network or other errors - retry with exponential backoff
        if (retryCount < 2) {
          const delay = Math.pow(2, retryCount) * 1000; // 1s, 2s
          console.warn(`Failed to load airports, retrying in ${delay}ms (attempt ${retryCount + 1}/2):`, err);
          setTimeout(() => {
            loadAirports(retryCount + 1);
          }, delay);
          // Keep loading state true during retry - don't clear airports or set error
          return;
        } else {
          // Max retries reached
          setError('Failed to load airports. Please try again.');
          setAirports([]);
          setLoadingAirports(false);
        }
      }
    }
  }, [isAuthenticated, isManager, user]);

  // Load airports when dialog opens; default project start to next business day
  useEffect(() => {
    if (open && !authLoading && isAuthenticated) {
      setProjectStartDate(getDefaultProjectStartDateString());
      const timer = setTimeout(() => {
        loadAirports();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open, authLoading, isAuthenticated, loadAirports]);

  const handleCreate = async () => {
    // Validate
    if (!projectName.trim()) {
      setError('Project name is required');
      return;
    }

    if (!projectStartDate) {
      setError('Project start date is required');
      return;
    }

    // AirportId is now required
    if (selectedAirportId === '' || selectedAirportId === null || selectedAirportId === undefined) {
      setError('Associated Airport is required');
      return;
    }

    setError(null);
    setIsCreating(true);

    try {
      // CRITICAL: Log the exact date value being sent - NO conversion, use native value
      console.log('CreateProjectDialog: Sending project data with date:',
        'projectStartDate=' + JSON.stringify(projectStartDate),
        'type=' + typeof projectStartDate,
        'NATIVE VALUE - NO CONVERSION'
      );
      
      const projectData = {
        name: projectName.trim(),
        projectStartDate: projectStartDate, // Already in YYYY-MM-DD format - use directly, NO conversion
        airportId: selectedAirportId as number, // Required - already validated above
      };

      await onCreate(projectData);
      
      // Reset form
      setProjectName('');
      setProjectStartDate(getDefaultProjectStartDateString());
      // Don't reset airport for Airport Managers - it will be auto-set when dialog reopens
      if (!isManager) {
        setSelectedAirportId('');
      }
      setError(null);
      onClose();
    } catch (err) {
      const errorMessage = (err && typeof err === 'object' && 'message' in err)
        ? (err as { message: string }).message
        : 'Failed to create project';
      setError(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    if (!isCreating) {
      setProjectName('');
      setProjectStartDate(getDefaultProjectStartDateString());
      // Don't reset airport for Airport Managers - it will be auto-set when dialog reopens
      if (!isManager) {
        setSelectedAirportId('');
      }
      setError(null);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Create New Project</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <TextField
            label="Project Name"
            value={projectName}
            onChange={(e) => setProjectName(e.target.value)}
            required
            fullWidth
            autoFocus
            disabled={isCreating}
            helperText="Enter a descriptive name for your project"
          />

          <TextField
            label="Project Start Date"
            type="date"
            value={(() => {
              console.log('CreateProjectDialog: TextField value:', JSON.stringify(projectStartDate));
              return projectStartDate;
            })()}
            onChange={(e) => {
              const newValue = e.target.value;
              console.log('CreateProjectDialog: TextField onChange:',
                'oldValue=' + JSON.stringify(projectStartDate),
                'newValue=' + JSON.stringify(newValue),
                'NATIVE VALUE - NO CONVERSION'
              );
              setProjectStartDate(newValue);
            }}
            required
            fullWidth
            disabled={isCreating}
            helperText="Select the planned start date for this project"
            InputLabelProps={{
              shrink: true,
            }}
          />

          <FormControl 
            fullWidth 
            disabled={isCreating || loadingAirports || isManager}
            required
            variant="outlined"
          >
            <InputLabel 
              id="airport-select-label"
              shrink={true}
            >
              Associated Airport
            </InputLabel>
            <Select
              labelId="airport-select-label"
              id="airport-select"
              value={selectedAirportId}
              label="Associated Airport"
              onChange={(e) => setSelectedAirportId(e.target.value as number | '')}
              displayEmpty
              renderValue={(selected: number | '') => {
                if (selected === '' || selected === null || selected === undefined) {
                  return <span style={{ color: '#999', fontStyle: 'italic' }}>Select an airport (required)</span>;
                }
                if (typeof selected === 'number' && selected === 0) {
                  return <span style={{ color: '#999', fontStyle: 'italic' }}>Select an airport (required)</span>;
                }
                const airportId = typeof selected === 'string' ? parseInt(selected, 10) : selected;
                const selectedAirport = airports.find(a => a.airportId === airportId);
                return selectedAirport ? selectedAirport.name : '';
              }}
            >
              <MenuItem value="">
                <em>Select an airport (required)</em>
              </MenuItem>
              {loadingAirports ? (
                <MenuItem disabled>
                  <CircularProgress size={20} sx={{ mr: 1 }} />
                  Loading airports...
                </MenuItem>
              ) : (
                airports.map((airport) => (
                  <MenuItem key={airport.airportId} value={airport.airportId}>
                    {airport.name}
                  </MenuItem>
                ))
              )}
            </Select>
            <FormHelperText>
              {isManager 
                ? 'Automatically set to your managed airport' 
                : 'Select the airport associated with this project (required)'}
            </FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isCreating}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={isCreating || !projectName.trim() || !projectStartDate || selectedAirportId === ''}
        >
          {isCreating ? 'Creating...' : 'Create Project'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProjectDialog;

